import React, { useState, useRef, useEffect } from 'react';
import css from './SearchableSelect.module.css';

const SearchableSelect = ({ 
    options = [], 
    value, 
    onChange, 
    placeholder = "Select option...", 
    searchPlaceholder = "Type to search...",
    className = "",
    disabled = false,
    getOptionLabel = (option) => option.label || option.name || option,
    getOptionValue = (option) => option.value || option.id || option,
    isOptionDisabled = () => false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const optionsRef = useRef([]);

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Find selected option
    const selectedOption = options.find(option => getOptionValue(option) === value);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset highlighted index when filtered options change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [searchTerm]);

    const handleInputClick = () => {
        if (!disabled) {
            setIsOpen(true);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
        setHighlightedIndex(-1);
    };

    const handleOptionClick = (option) => {
        if (!isOptionDisabled(option)) {
            onChange(getOptionValue(option));
            setIsOpen(false);
            setSearchTerm('');
            setHighlightedIndex(-1);
        }
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
            
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;
            
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;
            
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    const selectedOption = filteredOptions[highlightedIndex];
                    if (!isOptionDisabled(selectedOption)) {
                        handleOptionClick(selectedOption);
                    }
                }
                break;
            
            default:
                break;
        }
    };

    // Scroll highlighted option into view
    useEffect(() => {
        if (highlightedIndex >= 0 && optionsRef.current[highlightedIndex]) {
            optionsRef.current[highlightedIndex].scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [highlightedIndex]);

    return (
        <div className={`${css.container} ${className}`} ref={containerRef}>
            <div 
                className={`${css.input} ${disabled ? css.disabled : ''} ${isOpen ? css.open : ''}`} 
                onClick={handleInputClick}
            >
                {isOpen ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={searchPlaceholder}
                        className={css.searchInput}
                        autoFocus
                    />
                ) : (
                    <span className={css.selectedValue}>
                        {selectedOption ? getOptionLabel(selectedOption) : placeholder}
                    </span>
                )}
                <div className={`${css.arrow} ${isOpen ? css.arrowUp : css.arrowDown}`}>▼</div>
            </div>

            {isOpen && (
                <div className={css.dropdown}>
                    {filteredOptions.length === 0 ? (
                        <div className={css.noResults}>No results found</div>
                    ) : (
                        filteredOptions.map((option, index) => (
                            <div
                                key={getOptionValue(option)}
                                ref={el => optionsRef.current[index] = el}
                                className={`${css.option} ${
                                    highlightedIndex === index ? css.highlighted : ''
                                } ${
                                    isOptionDisabled(option) ? css.optionDisabled : ''
                                }`}
                                onClick={() => handleOptionClick(option)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {getOptionLabel(option)}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export { SearchableSelect }; 